import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout, PageHeader } from '../components/Layout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Users, 
  FileText, 
  CheckCircle2, 
  Clock, 
  TrendingUp,
  ArrowRight,
  Activity
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, submissionsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/stats`),
        axios.get(`${API_URL}/admin/submissions`)
      ]);
      
      setStats(statsRes.data);
      // Get 5 most recent submissions
      const sorted = submissionsRes.data.sort((a, b) => 
        new Date(b.updated_at) - new Date(a.updated_at)
      );
      setRecentSubmissions(sorted.slice(0, 5));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-500">Loading dashboard...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <PageHeader
          title="Admin Dashboard"
          description="Overview of client submissions and activity."
        />

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bento-card" data-testid="stat-users">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-[#FF6B35]" />
              </div>
              <TrendingUp className="h-5 w-5 text-emerald-500" />
            </div>
            <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {stats?.total_users || 0}
            </div>
            <p className="text-sm text-slate-500 mt-1">Total Users</p>
          </div>

          <div className="bento-card" data-testid="stat-total-submissions">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {stats?.total_submissions || 0}
            </div>
            <p className="text-sm text-slate-500 mt-1">Total Submissions</p>
          </div>

          <div className="bento-card" data-testid="stat-completed-submissions">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {stats?.completed_submissions || 0}
            </div>
            <p className="text-sm text-slate-500 mt-1">Completed</p>
          </div>

          <div className="bento-card" data-testid="stat-pending-submissions">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {stats?.pending_submissions || 0}
            </div>
            <p className="text-sm text-slate-500 mt-1">In Progress</p>
          </div>
        </div>

        {/* Submissions by Template */}
        {stats?.by_template && Object.keys(stats.by_template).length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Submissions by Template
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(stats.by_template).map(([name, count]) => (
                <div key={name} className="bg-slate-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-[#FF6B35]">{count}</p>
                  <p className="text-sm text-slate-600 mt-1">{name}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Recent Submissions
            </h2>
            <Link to="/admin/submissions">
              <Button variant="ghost" size="sm" className="gap-1">
                View All
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>

          {recentSubmissions.length === 0 ? (
            <div className="p-12 text-center">
              <Activity className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No submissions yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recentSubmissions.map((submission) => (
                <div 
                  key={submission.id} 
                  className="px-6 py-4 hover:bg-slate-50 transition-colors"
                  data-testid={`recent-submission-${submission.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        submission.is_completed ? 'bg-emerald-100' : 'bg-amber-100'
                      }`}>
                        {submission.is_completed ? (
                          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        ) : (
                          <Clock className="h-5 w-5 text-amber-600" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-medium text-slate-900">{submission.template_name}</h3>
                        <p className="text-sm text-slate-500">{submission.company_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={submission.is_completed ? 'status-success' : 'status-pending'}>
                        {submission.is_completed ? 'Completed' : 'In Progress'}
                      </span>
                      <span className="text-sm text-slate-500">
                        {new Date(submission.updated_at).toLocaleDateString()}
                      </span>
                      <Link to={`/admin/submissions/${submission.id}`}>
                        <Button variant="ghost" size="sm" data-testid={`view-submission-${submission.id}`}>
                          View
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
